import React, { useState, useEffect, useRef, useCallback } from 'react';
import adsApi from '../services/google-ads';
import './SeaCampaignBuilder.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const SUGGESTIONS = [
  "Je veux promouvoir nos pièces détachées Opel en Guadeloupe, budget ~30 €/j.",
  "Analyse la concurrence sur l'entretien auto dans ma zone.",
  "Propose-moi une structure de groupes d'annonces.",
  "Trouve les mots-clés les plus pertinents avec leurs volumes.",
];

// Markdown léger
function fmtInline(t) {
  return t.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : p);
}
function fmtMsg(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('### ')) return <h4 key={i} className="scb-md-h">{fmtInline(line.slice(4))}</h4>;
    if (line.startsWith('## ')) return <h4 key={i} className="scb-md-h">{fmtInline(line.slice(3))}</h4>;
    if (line.match(/^[-•*]\s/)) return <li key={i} className="scb-md-li">{fmtInline(line.slice(2))}</li>;
    if (line.match(/^\d+\.\s/)) return <li key={i} className="scb-md-li">{fmtInline(line.replace(/^\d+\.\s/, ''))}</li>;
    if (!line.trim()) return <br key={i} />;
    return <p key={i} className="scb-md-p">{fmtInline(line)}</p>;
  });
}

const BID_LABELS = { MAXIMIZE_CLICKS: 'Maximiser les clics', MANUAL_CPC: 'CPC manuel', MAXIMIZE_CONVERSIONS: 'Maximiser les conversions' };

// Compteur de caractères (vert/rouge selon limite)
function CharCount({ value, max }) {
  const len = (value || '').length;
  return <span className={`scb-cc ${len > max ? 'over' : ''}`}>{len}/{max}</span>;
}

export default function SeaCampaignBuilder({ customer, accountName, isSuperAdmin }) {
  const [drafts, setDrafts] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [data, setData] = useState(null); // { draft, validation, messages }
  const [loadingDraft, setLoadingDraft] = useState(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const [activeTool, setActiveTool] = useState(null);

  const [building, setBuilding] = useState(false);
  const [buildMsg, setBuildMsg] = useState('');
  const [buildErr, setBuildErr] = useState('');
  const endRef = useRef(null);

  const loadDrafts = useCallback(async () => {
    if (!customer) return;
    try { const r = await adsApi.seaListDrafts(customer); setDrafts(r.data || []); }
    catch { setDrafts([]); }
  }, [customer]);

  const loadDraft = useCallback(async (id) => {
    if (!id) { setData(null); setMessages([]); return; }
    setLoadingDraft(true);
    try {
      const r = await adsApi.seaGetDraft(id);
      setData(r.data);
      setMessages((r.data.messages || []).map(m => ({ role: m.role, content: m.content })));
    } catch { setData(null); }
    finally { setLoadingDraft(false); }
  }, []);

  useEffect(() => { setDraftId(null); setData(null); setMessages([]); loadDrafts(); }, [customer, loadDrafts]);
  useEffect(() => { loadDraft(draftId); setBuildMsg(''); setBuildErr(''); }, [draftId, loadDraft]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streamContent]);

  const createDraft = async () => {
    try { const r = await adsApi.seaCreateDraft(customer, 'Nouvelle campagne SEA'); await loadDrafts(); setDraftId(r.data.id); }
    catch (e) { setBuildErr(e.response?.data?.error || 'Création du brouillon impossible.'); }
  };

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || streaming || !draftId) return;
    const next = [...messages, { role: 'user', content }];
    setMessages(next); setInput(''); setStreaming(true); setStreamContent(''); setActiveTool(null);
    const history = next.slice(-12);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/google-ads/sea/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ customer, draft_id: draftId, message: content, history }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = '', buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const s = line.slice(6).trim(); if (!s) continue;
          let ev; try { ev = JSON.parse(s); } catch { continue; }
          if (ev.type === 'token') { acc += ev.content; setStreamContent(acc); }
          else if (ev.type === 'tool_start') setActiveTool(ev.name);
          else if (ev.type === 'tool_done') setActiveTool(null);
          else if (ev.type === 'draft_changed') loadDraft(draftId);
          else if (ev.type === 'done') { const c = acc; if (c) setMessages(p => [...p, { role: 'assistant', content: c }]); setStreamContent(''); }
          else if (ev.type === 'error') { const c = ev.content || 'Erreur.'; setMessages(p => [...p, { role: 'assistant', content: c }]); setStreamContent(''); }
        }
      }
      if (acc) setMessages(p => (p[p.length - 1]?.content === acc ? p : [...p, { role: 'assistant', content: acc }]));
      setStreamContent('');
    } catch {
      setMessages(p => [...p, { role: 'assistant', content: '❌ Erreur de connexion à l\'assistant.' }]);
      setStreamContent('');
    } finally { setStreaming(false); setActiveTool(null); loadDrafts(); }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const build = async () => {
    if (!draftId) return;
    setBuilding(true); setBuildErr(''); setBuildMsg('');
    try {
      const r = await adsApi.seaBuild(draftId);
      setBuildMsg('✅ Campagne créée EN PAUSE sur Google Ads. Activez-la depuis Google Ads après revue.');
      await loadDraft(draftId); await loadDrafts();
      if (r.data?.campaignResource) setBuildMsg(m => m + ` (${r.data.campaignResource})`);
    } catch (e) { setBuildErr(e.response?.data?.error || 'Échec de la création.'); }
    finally { setBuilding(false); }
  };

  const draft = data?.draft?.draft_json;
  const validation = data?.validation;
  const status = data?.draft?.status;
  const created = status === 'created';

  return (
    <div className="scb">
      <div className="scb-bar">
        <select className="page-select" value={draftId || ''} onChange={e => setDraftId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">— Choisir un brouillon —</option>
          {drafts.map(d => <option key={d.id} value={d.id}>{d.title} {d.status === 'created' ? '✅' : d.status === 'reverted' ? '↩' : ''}</option>)}
        </select>
        <button className="scb-new" onClick={createDraft}>+ Nouveau brouillon</button>
        <span className="scb-bar-acc">{accountName || '—'}</span>
      </div>

      {!draftId && (
        <div className="scb-empty">
          <div className="scb-empty-icon">🎯</div>
          <p>Crée un brouillon, puis décris ton objectif à l'assistant SEA.</p>
          <span>Claude analyse le marché, recherche les mots-clés et construit la campagne. La création reste validée par toi (campagne en pause).</span>
          <button className="scb-new" onClick={createDraft}>+ Démarrer une campagne</button>
        </div>
      )}

      {draftId && (
        <div className="scb-grid">
          {/* ── Conversation ── */}
          <section className="scb-chat">
            <div className="scb-chat-body">
              {messages.length === 0 && !streamContent && (
                <div className="scb-intro">
                  <p>Décris ce que tu veux promouvoir, ta zone et ton budget. Je te recommande une campagne complète.</p>
                  <div className="scb-suggestions">
                    {SUGGESTIONS.map((s, i) => <button key={i} className="scb-chip" onClick={() => send(s)}>{s}</button>)}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`scb-msg scb-msg-${m.role}`}>
                  <div className="scb-msg-content">{m.role === 'assistant' ? fmtMsg(m.content) : m.content}</div>
                </div>
              ))}
              {(streamContent || streaming) && (
                <div className="scb-msg scb-msg-assistant">
                  <div className="scb-msg-content">{streamContent ? fmtMsg(streamContent) : null}{streaming && <span className="scb-cursor" />}</div>
                  {activeTool && <div className="scb-tool">⏳ {activeTool}…</div>}
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className="scb-input">
              <textarea rows={1} placeholder="Décris ton besoin…" value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKey} disabled={streaming || created} />
              <button className="scb-send" onClick={() => send()} disabled={!input.trim() || streaming || created}>➤</button>
            </div>
            <p className="scb-disclaimer">Claude construit le brouillon — rien n'est créé sur Google sans ta validation. La campagne sera créée en pause.</p>
          </section>

          {/* ── Brouillon ── */}
          <section className="scb-draft">
            {loadingDraft && <div className="scb-muted">Chargement…</div>}
            {draft && (
              <>
                <div className="scb-card">
                  <div className="scb-card-h">Campagne</div>
                  <div className="scb-kv"><span>Nom</span><b>{draft.campaign?.name || <i className="scb-todo">à définir</i>}</b></div>
                  <div className="scb-kv"><span>Budget /jour</span><b>{draft.campaign?.dailyBudget ? `${draft.campaign.dailyBudget} €` : <i className="scb-todo">—</i>}</b></div>
                  <div className="scb-kv"><span>Enchères</span><b>{BID_LABELS[draft.campaign?.bidStrategy] || draft.campaign?.bidStrategy}</b></div>
                  <div className="scb-kv"><span>Réseau</span><b>Search{draft.campaign?.searchPartners ? ' + partenaires' : ''}</b></div>
                  <div className="scb-kv"><span>Ciblage</span><b>{(draft.targeting?.locations || []).map(l => l.name).join(', ') || <i className="scb-todo">—</i>} · {(draft.targeting?.languages || []).map(l => l.code).join('/')}</b></div>
                </div>

                {(draft.adGroups || []).map((g, gi) => (
                  <div key={gi} className="scb-card">
                    <div className="scb-card-h">Groupe « {g.name} » <span className="scb-badge">{g.keywords?.length || 0} mots-clés</span></div>
                    <div className="scb-kw">
                      {(g.keywords || []).map((k, ki) => <span key={ki} className="scb-kwchip">{k.text}<em>{k.matchType?.[0]}</em></span>)}
                      {!(g.keywords || []).length && <i className="scb-todo">aucun mot-clé</i>}
                    </div>
                    {(g.ads || []).map((ad, ai) => (
                      <div key={ai} className="scb-rsa">
                        <div className="scb-rsa-h">Annonce RSA · <a href={ad.finalUrl} target="_blank" rel="noreferrer">{ad.finalUrl}</a></div>
                        <div className="scb-rsa-lines">
                          {ad.headlines.map((h, hi) => <div key={hi} className="scb-rsa-line">{h} <CharCount value={h} max={30} /></div>)}
                        </div>
                        <div className="scb-rsa-lines scb-rsa-desc">
                          {ad.descriptions.map((d, di) => <div key={di} className="scb-rsa-line">{d} <CharCount value={d} max={90} /></div>)}
                        </div>
                      </div>
                    ))}
                    {!(g.ads || []).length && <i className="scb-todo">aucune annonce RSA</i>}
                  </div>
                ))}

                {(draft.negativeKeywords || []).length > 0 && (
                  <div className="scb-card">
                    <div className="scb-card-h">Mots-clés négatifs <span className="scb-badge">{draft.negativeKeywords.length}</span></div>
                    <div className="scb-kw">{draft.negativeKeywords.map((k, i) => <span key={i} className="scb-kwchip neg">{k.text}</span>)}</div>
                  </div>
                )}

                {/* Validation + création */}
                {created ? (
                  <div className="scb-status-ok">✅ Campagne créée (en pause){data.draft.created_campaign_resource ? ` · ${data.draft.created_campaign_resource}` : ''}.{isSuperAdmin && <button className="scb-revert" onClick={async () => { try { await adsApi.seaRevert(draftId); await loadDraft(draftId); await loadDrafts(); } catch (e) { setBuildErr(e.response?.data?.error || 'Annulation impossible.'); } }}>↩ Annuler la création</button>}</div>
                ) : (
                  <div className="scb-finish">
                    {validation && !validation.valid && (
                      <ul className="scb-valid">{validation.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                    )}
                    {buildErr && <div className="scb-err">{buildErr}</div>}
                    {buildMsg && <div className="scb-ok">{buildMsg}</div>}
                    {isSuperAdmin ? (
                      <button className="scb-build" disabled={!validation?.valid || building} onClick={build}>
                        {building ? 'Vérification Google…' : '✅ Valider & créer la campagne (en pause)'}
                      </button>
                    ) : (
                      <div className="scb-muted">La création réelle est réservée aux super-admins. Le brouillon {validation?.valid ? 'est prêt' : 'reste à compléter'}.</div>
                    )}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
